
import {
  Component,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  DOCUMENT
} from '@angular/core';
import { ktdArrayRemoveItem } from '@components/pages/grid-container/ktd-array-remove-item';
import {
  WidgetConfigurationModalComponent
} from '@components/pages/widgets/widget-configuration-modal/widget-configuration-modal.component';
import { UserDefinedWidgetType } from '@constants/pages/user-defined-page/user-defined-widget-type';
import {
  KtdGridComponent,
  KtdGridLayout,
  KtdResizeEnd,
  KtdResizeStart,
  KtdGridCompactType,
  ktdTrackById
} from '@katoid/angular-grid-layout';
import { Machine } from '@models/machine/machine';
import { GridContainerSettings } from '@models/pages/grid-container-settings';
import { GridContainerTransitionType } from '@models/pages/grid-container-transition-type';
import { ChartSignalEntry } from '@models/pages/user-defined-page/chart-signal-entry';
import { UserDefinedWidget } from '@models/pages/user-defined-page/user-defined-widget';
import { UserPageGridLayoutItem } from '@models/pages/user-page-grid-layout-item';
import { MenuItem } from '@models/sidebar/menu-item';
import { ArrayOperationService } from '@services/administration/array-operation/array-operation.service';
import { ModalService } from '@services/modal/modal.service';
import { SideBarService } from '@services/side-bar/side-bar.service';
import { cloneDeep } from 'lodash';
import { fromEvent, merge, Subscription } from 'rxjs';

@Component({
    selector: 'scitis-grid-container',
    templateUrl: './grid-container.component.html',
    styleUrls: ['./grid-container.component.scss'],
    standalone: false
})
export class GridContainerComponent implements OnInit, OnChanges, OnDestroy {

  @Input()
  isInEditMode: boolean = false;
  @Input()
  shouldAddWidget: number = -1;
  @Input()
  widgets: UserDefinedWidget[] = [];
  @Input()
  firebaseUserId: string = '';
  @Input()
  machine: Machine = new Machine();

  @Input()
  menuItem: MenuItem = new MenuItem();
  @Input()
  isOnTemplatePage: boolean = false;

  @Output()
  updatedLayout: EventEmitter<UserPageGridLayoutItem[]> = new EventEmitter<UserPageGridLayoutItem[]>();

  @Output()
  newWidget: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  moveToInteractiveDataAnalysisEvent: EventEmitter<ChartSignalEntry[]> = new EventEmitter<ChartSignalEntry[]>();

  @Output()
  deleteWidget: EventEmitter<[string, string]> = new EventEmitter<[string, string]>();

  @ViewChild(KtdGridComponent, {static: true})
  grid: KtdGridComponent | undefined;

  trackById = ktdTrackById;
  layout: UserPageGridLayoutItem[] = [];
  layoutBackup: UserPageGridLayoutItem[] = [];

  gridSettings: GridContainerSettings = new GridContainerSettings();
  gridColumns: number = this.gridSettings.columns;
  gridCompactType: KtdGridCompactType = this.gridSettings.compactTypes[0].value;
  gridTransitions: GridContainerTransitionType[] = this.gridSettings.transitions;
  currentTransition: string = this.gridTransitions[0].value;

  public isResizeEnded: Record<string, any> = {};

  resizeSubscription: Subscription = new Subscription();
  public isPageVisible: boolean = true;
  public forceHighChartsChartReflow: boolean = false;

  private readonly MIN_WIDGET_WIDTH: number = 2;
  constructor(@Inject(DOCUMENT) public document: Document,
              private modalService: ModalService,
              private sideBarService: SideBarService,
              private arrayOperationService: ArrayOperationService) {
  }

  get widgetNames(): string[] {
    const existingWidgetNames: string[] = [];
    for (const widget of this.widgets) {
      existingWidgetNames.push(widget.widgetDetails.widgetName);
    }
    return existingWidgetNames;
  }

  @HostListener('document:visibilitychange', ['$event'])
  visibilitychange() {
    this.isPageVisible = !document.hidden;
  }

  ngOnInit() {
    this.listenForResizeEvent();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.widgets) {
      this.layoutBackup = cloneDeep(this.layout);
      this.transformWidgetsToLayoutItems(changes.widgets.firstChange);
      this.setGridColumnsByWindowSize();
    }
    if (changes?.isInEditMode) {
      if (changes.isInEditMode.currentValue) {
        this.gridSettings.disableDrag = false;
        this.gridSettings.disableRemove = false;
        this.gridSettings.disableResize = false;
      } else {
        this.gridSettings.disableDrag = true;
        this.gridSettings.disableRemove = true;
        this.gridSettings.disableResize = true;
      }
    }

    if (changes?.shouldAddWidget?.currentValue >= 0) {
      this.addItemToLayout();
    }
  }

  ngOnDestroy() {
    this.resizeSubscription.unsubscribe();
  }

  public addItemToLayout() {
    if (!this.machine?.id) {
      return;
    }
    const newWidget = new UserDefinedWidget();
    newWidget.machineId = this.machine.id;

    const modalRef = this.modalService.open(WidgetConfigurationModalComponent, {size: 'lg'});
    modalRef.componentInstance.isAddOperation = true;
    modalRef.componentInstance.userDefinedWidget = newWidget;
    modalRef.componentInstance.machine = this.machine;
    modalRef.componentInstance.existingWidgetNames = this.widgetNames;
    modalRef.componentInstance.existingWidgetTypes = this.checkExistingWidgetsTypes();
    modalRef.result.then((result: UserDefinedWidget) => {
      this.newWidget.emit(result);
      this.addWidgetWithCoordinates(result.widgetDetails.widgetName, result.widgetDetails.widgetType);
    });
  }

  public getWidget(layoutItem: UserPageGridLayoutItem): UserDefinedWidget {
    for (const widget of this.widgets) {
      if (String(widget.id) === layoutItem.id ||
          (widget.widgetDetails.widgetName === layoutItem.name && widget.widgetDetails.widgetType === layoutItem.type)) {
        return widget;
      }
    }
    return new UserDefinedWidget();
  }

  public onResizeStarted(event: KtdResizeStart) {
    this.isResizeEnded[event.gridItemRef.id] = false;
  }

  public onResizeEnded(_event: KtdResizeEnd) {
    //not used
  }

  public onTransitionStart(_id: string, _event: TransitionEvent) {
    //not used
  }

  public onTransitionEnd(id: string, _event: TransitionEvent) {
    this.isResizeEnded[id] = true;
  }

  public onLayoutUpdated(layout: KtdGridLayout) {
    console.log('on layout updated', layout);
    this.updateLayoutOnChange(this.layout, layout);
  }


  onWidgetRemove([chartId, chartName]: [string, string]) {
    // Important: Don't mutate the array. Let Angular know that the layout has changed creating a new reference.
    this.layout = ktdArrayRemoveItem(this.layout, (item) => item.id === chartId);
    this.updatedLayout.emit(this.layout);
    this.deleteWidget.emit([chartId, chartName]);
  }

  onMoveToInteractiveDataAnalysis(event: ChartSignalEntry[]) {
    this.moveToInteractiveDataAnalysisEvent.emit(event);
  }

  private setGridColumnsByWindowSize() {
    const innerWidth = window.innerWidth;
    if (innerWidth > this.gridSettings.columnRanges.defaultColumn.min) {
      this.setGridItemsSettingsAndColumns(this.gridSettings.columnRanges.defaultColumn.size);
    } else {
      this.setGridItemsSettingsAndColumns(this.gridSettings.columnRanges.singleColumn.size);
    }
  }

  private setGridItemsSettingsAndColumns(columns: number) {
    const updatedLayout = [...cloneDeep(this.layoutBackup)];
    for (const item of updatedLayout) {
      let width, minWidth, maxWidth;
      switch (columns) {
        case this.gridSettings.columnRanges.defaultColumn.size:
          width = item.w; //original width
          minWidth = item.minW;
          maxWidth = item.maxW;
          break;
        case this.gridSettings.columnRanges.singleColumn.size:
          width = this.gridSettings.columnRanges.singleColumn.size;
          minWidth = width;
          maxWidth = width;
          break;
      }
      item.w = width;
      item.withMinW(minWidth);
      item.withMaxW(maxWidth);
    }
    this.gridColumns = columns;
    this.layout = [...updatedLayout];
    this.gridCompactType = this.gridSettings.compactTypes[1].value;
    setTimeout(() => {
      this.gridCompactType = this.gridSettings.compactTypes[0].value;
    }, 50);

  }

  private transformWidgetsToLayoutItems(firstChange: boolean) {
    if (!firstChange) {
      return;
    }
    this.layout = [];
    for (const widget of this.widgets) {
      const userPageGridLayoutItem = new UserPageGridLayoutItem(String(widget.id), widget.widgetDetails.topX,
          widget.widgetDetails.topY, Math.max(widget.widgetDetails.width, this.MIN_WIDGET_WIDTH), widget.widgetDetails.height,
          widget.widgetDetails.widgetName, widget.widgetDetails.widgetType);
      userPageGridLayoutItem.withMinW(this.MIN_WIDGET_WIDTH);
      this.layout.push(userPageGridLayoutItem);
    }
    this.layoutBackup = cloneDeep(this.layout);
    this.updatedLayout.emit(this.layout);
  }

  private getNextAvailableId(): number {
    const maxId = this.layout.reduce(
        (acc, cur) => Math.max(acc, parseInt(cur.id, 10)),
        -1
    );
    return maxId + 1;
  }

  private addWidgetWithCoordinates(name: string, type: UserDefinedWidgetType): void {
    const id = this.getNextAvailableId();
    const minWidth = Math.min(this.gridColumns, this.MIN_WIDGET_WIDTH);
    const newLayoutItem = new UserPageGridLayoutItem(id.toString(), 0, 0, minWidth, 3, name, type);
    newLayoutItem.withMinW(minWidth);
    if (this.layout.length !== 0) {
      const tallestObject = this.layout.reduce((prev, current) => prev.y > current.y ? prev : current);
      newLayoutItem.y = tallestObject.h + tallestObject.y;
    }

    this.layout = [...this.layout, newLayoutItem];
    this.layoutBackup = cloneDeep(this.layout);
    this.updatedLayout.emit(this.layout);
    this.scrollWidgetIntoView(name, id);
  }

  private scrollWidgetIntoView(widgetName: string, widgetId: number) {
    setTimeout(() => {
      const elementById = document.getElementById(widgetName + '_' + widgetId);
      if (elementById) {
        elementById.scrollIntoView(true);
      }
    }, 100);
  }

  private updateLayoutOnChange(currentLayout: UserPageGridLayoutItem[], updatedLayout: KtdGridLayout) {
    for (const updated of updatedLayout) {
      const id = updated.id;
      for (let existingLayoutItem of currentLayout) {
        if (existingLayoutItem.id === id) {
          existingLayoutItem = new UserPageGridLayoutItem(id, updated.x, updated.y, Math.max(updated.w, this.MIN_WIDGET_WIDTH), updated.h, existingLayoutItem.name, existingLayoutItem.type);
          existingLayoutItem.withMinW(this.MIN_WIDGET_WIDTH);
          this.arrayOperationService.updateItem(existingLayoutItem, this.layout);
        }
      }
    }
    this.updatedLayout.emit(this.layout);
  }

  private listenForResizeEvent(): void {
    this.listenForWindowResize();
    this.listenForSidebarStateChange();
  }

  private listenForWindowResize(): void {
    this.resizeSubscription = merge(
        fromEvent(window, 'resize'),
        fromEvent(window, 'orientationchange')
    ).subscribe(() => {
      this.setGridColumnsByWindowSize();
      this.resizeGrid();
    });
  }

  private listenForSidebarStateChange(): void {
    this.sideBarService.sidebarStateChange.subscribe((_value) => {
      this.resizeGrid();
    });
  }

  private resizeGrid(): void {
    this.forceHighChartsChartReflow = false;
    setTimeout(() => {
      this.forceHighChartsChartReflow = true;
      if (this.grid) {
        this.grid.resize();
      }
    }, 250);
  }

  private checkExistingWidgetsTypes(): UserDefinedWidgetType[] {
    const arr: UserDefinedWidgetType[] = [];
    this.widgets.reduce((acc, {widgetDetails}) => {
      if (!acc.includes(widgetDetails.widgetType)) {
        acc.push(widgetDetails.widgetType);
      }
      return acc;
    }, arr);
    return arr;
  }
}
